import type {
  FormattedUnsettlementsActions,
  TransformedUnsettlementsActionsResponses,
  UnsettlementsActionsResponse
} from '@library/types/unsettlements';
import {
  getDateTime,
  getFormattedDateTime
} from '@bo-utils/members/memberCommon';
import type { SingleProjectionAPIResponse } from '@library/types/projection';

export const transformUnsettlementsActionsResponse = ({
  page,
  perPage,
  response
}: {
  page: number;
  perPage: number;
  response: UnsettlementsActionsResponse | undefined;
}): FormattedUnsettlementsActions => {
  if (!response) {
    return {
      currentPage: 0,
      data: [],
      totalPages: 0
    };
  }

  const unsettlementsActionsincluded = response.included || [];
  const transformedUnsettlementsActionsdata: TransformedUnsettlementsActionsResponses =
    response.data?.map((dataSet) => {
      const { attributes, relationships } = dataSet;
      const {
        created_at: createdAt,
        finished_at: completedOn,
        id: actionId,
        projection_id: projectionId,
        unsettlement_reason: reason,
        updated_at: updatedAt
      } = attributes;
      const {
        admin_user: {
          data: { id: adminId }
        }
      } = relationships;

      // filters the included array to get the admin user data that matches the adminId
      const adminUserData = unsettlementsActionsincluded.find((item) => {
        return item.id.toString() === adminId;
      });

      const { first_name: firstName = '', last_name: lastName = '' } =
        adminUserData?.attributes || {};

      const adminName = `${firstName || ''} ${lastName || ''}`;

      const completedDateTime = getDateTime({ dateString: completedOn });
      const createdDateTime = getDateTime({ dateString: createdAt });
      const updatedDateTime = getDateTime({ dateString: updatedAt });

      const { formattedDate: completedDate, formattedTime: completedTime } =
        getFormattedDateTime({ dateTime: completedDateTime });

      const { formattedDate: createdDate, formattedTime: createdTime } =
        getFormattedDateTime({ dateTime: createdDateTime });

      const { formattedDate: updatedDate, formattedTime: updatedTime } =
        getFormattedDateTime({ dateTime: updatedDateTime });

      const transformedData = {
        actionId,
        adminName,
        completedDate,
        completedTime,
        createdDate,
        createdTime,
        projectionId,
        reason,
        updatedDate,
        updatedTime
      };

      return transformedData;
    });

  const totalPages =
    transformedUnsettlementsActionsdata.length < perPage ? page : page + 1;

  return {
    currentPage: page,
    data: transformedUnsettlementsActionsdata,
    totalPages
  };
};

export const formatProjectionScoreData = (
  projection: SingleProjectionAPIResponse | undefined
) => {
  const scoreDataItem = projection?.included.filter(
    (item) => item.type === 'score'
  )[0];

  const playerDetailsItem = projection?.included.filter(
    (item) => item.type === 'new_player'
  )[0];

  const projectedScore = projection?.data.attributes.line_score.toString();
  if (!scoreDataItem || !playerDetailsItem || projectedScore === undefined) {
    return undefined;
  }
  const playerDetails = playerDetailsItem.attributes;
  const opposingTeam = projection?.data.attributes.description;
  const scoreData = scoreDataItem.attributes;
  const teamDetails = `${playerDetails.team} vs ${opposingTeam}`;

  const formattedScoreData = {
    isDnp: scoreData?.is_off_the_board,
    isReboot: scoreData?.unders_win_dnp,
    playerImgUrl: playerDetails?.image_url,
    playerName: playerDetails?.display_name,
    projectedScore,
    score: scoreData?.score,
    teamDetails
  };

  return formattedScoreData;
};
